import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getExperimentFromState, getParameter, isExperimentActive } from '../store/experiments/selectors';
import { EventName } from '../tracking/EventName';
import { getTracker } from '../tracking/getTracker';
import { hasTrackingBeenEnabled } from '../tracking/trackingUtils';
import { VARIANT, VARIANT_A, VARIANT_B, VARIANT_C, VARIANT_D } from './experimentDefaults';
import { getExperiment } from './Experiments';
import { experimentsEnabled } from './ExperimentUtils';
export const TrackWhen = {
  assignedToVariant: 'assignedToVariant',
  assignedToControl: 'assignedToControl',
  enrolled: 'enrolled',
  never: 'never'
};

function getShouldTrackExposure(trackWhen, isAssignedToVariant) {
  switch (trackWhen) {
    case TrackWhen.assignedToVariant:
      return isAssignedToVariant();

    case TrackWhen.assignedToControl:
      return !isAssignedToVariant();

    case TrackWhen.never:
      return false;

    case TrackWhen.enrolled:
    default:
      return true;
  }
}

let HasExposureBeenTrackedCache = {};
export function getHasExposureBeenTracked(experimentId) {
  return HasExposureBeenTrackedCache[experimentId];
}

function setHasExposureBeenTracked(experimentId) {
  HasExposureBeenTrackedCache[experimentId] = true;
}

export function resetExposureTrackingForTesting() {
  HasExposureBeenTrackedCache = {};
}
export const useExperiment = (experimentId, trackWhen) => {
  const maybeExperimentObj = useMemo(() => getExperiment(experimentId), [experimentId]);
  const experiment = useSelector(state => getExperimentFromState(state, experimentId));
  const parameter = useSelector(state => getParameter(state, experimentId));
  const experimentActive = useSelector(state => isExperimentActive(state, experimentId));
  const shouldEnroll = useSelector(state => {
    return maybeExperimentObj && experimentsEnabled() && maybeExperimentObj.shouldEnroll(state);
  });
  const isAssignedToVariant = useCallback(() => {
    const variants = [VARIANT, VARIANT_A, VARIANT_B, VARIANT_C, VARIANT_D];
    return variants.includes(parameter) && experimentActive;
  }, [experimentActive, parameter]);
  useEffect(() => {
    const trackingEnabled = hasTrackingBeenEnabled();
    const exposureHasBeenTracked = getHasExposureBeenTracked(experimentId);
    const shouldTrackExposure = getShouldTrackExposure(trackWhen, isAssignedToVariant);

    if (trackingEnabled && experiment && shouldEnroll && !exposureHasBeenTracked && shouldTrackExposure) {
      setHasExposureBeenTracked(experimentId);
      getTracker().track(EventName.ExperimentExposure, {
        action: `${experimentId}-group-${experiment.parameters.group}`
      }, true);
    }
  }, [experiment, experimentActive, experimentId, isAssignedToVariant, shouldEnroll, trackWhen]);
  return {
    parameter,
    isExperimentActive: experimentActive,
    isAssignedToVariant
  };
};